import React, { useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useRouteMatch } from "react-router";
import { KSelectAutocomplete, KTextInput } from "@kyburz/kdesign-system";
import { makeStyles } from "@material-ui/core/styles";
import { setAlert } from "../../redux/actions/actions";
import {
  getIoTKeyDevice,
  getIoTKeyDeviceConfigFileUrl,
  updateIoTKeyDevice,
} from "../../apiCalls/config/iotKeyDevices";
import useSetState from "../../hooks/useSetState";
import BaseLayout from "../../components/layouts/BaseLayout";
import KFormBackground from "../../components/ui/KFormBackground";
import { getCustomers } from "../../apiCalls/auth";
import { formatDate } from "../../utils/timeUtils";
import BaseUploader from "../../components/BaseUploader/BaseUploader";
import {
  formatErrorResponse,
  validateMacAddress,
} from "../../utils/stringUtils";

const useStyles = makeStyles(() => ({
  field: {
    marginRight: 24,
  },
  block: {
    display: "flex",
    marginBottom: 16,
  },
  uploader: {
    display: "none",
  },
}));

export const BLE_DEVICE_TYPES = [
  {
    label: "vehicle_locker",
  },
  {
    label: "omni_locker",
  },
];

const IoTKeyDeviceView = () => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const inputFile = useRef<HTMLInputElement | null>(null);

  const onUploadClick = () => {
    inputFile?.current?.click();
  };

  const history: any = useHistory();
  const match = useRouteMatch({
    path: "/config/iot_key_devices/device/:id",
    strict: true,
    sensitive: true,
  });
  const { id } = match?.params as any;

  const [
    {
      created,
      modified,
      _id,
      company_id,
      iot_key_id,
      ble_device_type,
      mac_address,
      firmware_version,
      hardware_version,
      firmware_config_file,
      vehicle_id,
      formControlCustomers,
    },
    setState,
  ] = useSetState({
    created: "",
    modified: "",
    _id: "",
    company_id: "",
    iot_key_id: "",
    ble_device_type: null as null | string,
    mac_address: null as null | string,
    firmware_version: "",
    hardware_version: "",
    firmware_config_file: "",
    vehicle_id: "",
    formControlCustomers: [] as { label: string }[],
  });

  const isMacAddressValid = !mac_address || validateMacAddress(mac_address);

  const getFilename = (url: string) => {
    if (url.startsWith("https://fleetiotkey.s3.amazonaws.com")) {
      return new URL(url).pathname.replace(/^.*[\\/]/, "");
    }
    return "No file";
  };

  useEffect(() => {
    getIoTKeyDevice(id)
      .then((res) => {
        if (res && res.data) {
          setState({
            created: res.data.created || "",
            modified: res.data.modified || "",
            firmware_version: res.data.firmware_version || "",
            _id: res.data._id || "",
            ble_device_type: res.data.ble_device_type || null,
            mac_address: res.data.mac_address || null,
            hardware_version: res.data.hardware_version || "",
            company_id: res.data.company_id || "",
            iot_key_id: res.data.iot_key_id || "",
            vehicle_id: res.data.vehicle_id || "",
            firmware_config_file: res.data.firmware_config_file || "",
          });
        }
        return res;
      })
      .catch((e) => {
        history.push("/config/iot_key_devices/devices");
        dispatch(setAlert("This device does not exist", "error"));
        console.error(e);
      });
  }, []);

  useEffect(() => {
    getCustomers().then((res) => {
      setState({
        formControlCustomers: res.data
          .filter((el) => el._id !== null)
          .map((el) => ({ label: el.shortname })),
      });
    });
  }, []);

  const handleChange = (e) => setState({ [e.target.name]: e.target.value });

  const handleSelectAutoComplete = (name, value) => {
    setState({ [name]: value.label });
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      const res = await updateIoTKeyDevice({
        created,
        modified,
        _id,
        company_id,
        iot_key_id,
        ble_device_type,
        mac_address,
        firmware_version,
        hardware_version,
        firmware_config_file,
        vehicle_id,
      });
      setState({
        created: res.data.data.created || "",
        modified: res.data.data.modified || "",
        firmware_version: res.data.data.firmware_version || "",
        _id: res.data.data._id || "",
        ble_device_type: res.data.data.ble_device_type || null,
        mac_address: res.data.data.mac_address || null,
        hardware_version: res.data.data.hardware_version || "",
        company_id: res.data.data.company_id || "",
        iot_key_id: res.data.data.iot_key_id || "",
        vehicle_id: res.data.data.vehicle_id || "",
        firmware_config_file: res.data.data.firmware_config_file || "",
      });
      dispatch(setAlert("Saved successfully", "success"));
    } catch (error) {
      dispatch(setAlert(formatErrorResponse(error), "error"));
    }
  };

  const firmwareConfigFileName = useMemo<string>(
    () => getFilename(firmware_config_file),
    [firmware_config_file],
  );

  return (
    <BaseLayout
      title="IoT Key Device Overview"
      handleArrowClick={() => history.goBack()}
    >
      <KFormBackground
        size="large"
        primaryCTAColor="primary"
        primaryCTAonClick={handleSubmit}
        primaryCTAText="Save"
        secondaryCTAText="Cancel"
        secondaryCTAonClick={() => history.goBack()}
        disabled={
          iot_key_id.length === 0 ||
          company_id.length === 0 ||
          !isMacAddressValid
        }
      >
        <div className={classes.block}>
          <KTextInput
            label="IoT Key Id"
            name="iot_key_id"
            value={iot_key_id}
            disabled
            rootClassName={classes.field}
            key="iot_key_id"
          />
          <KTextInput
            label="Id"
            name="id"
            value={_id}
            disabled
            rootClassName={classes.field}
            key="id"
          />
        </div>
        <div className={classes.block}>
          <KSelectAutocomplete
            label="Company"
            name="company_id"
            value={company_id}
            onChange={handleSelectAutoComplete}
            elements={formControlCustomers}
            placeholder="Company"
            key="company_id"
          />
        </div>
        <div className={classes.block}>
          <KSelectAutocomplete
            label="BLE device type"
            name="ble_device_type"
            value={ble_device_type}
            onChange={handleSelectAutoComplete}
            elements={BLE_DEVICE_TYPES}
            placeholder="BLE device type"
            key="ble_device_type"
            isClearable
          />
        </div>
        <div className={classes.block}>
          <KTextInput
            label="MAC address"
            name="mac_address"
            value={mac_address}
            rootClassName={classes.field}
            onChange={handleChange}
            placeholder="1A:2B:3C:4D:5E:6F"
            errorText="MAC address should have format '1A:2B:3C:4D:5E:6F'"
            error={!isMacAddressValid}
            required={false}
            key="mac_address"
          />
        </div>
        <div className={classes.block}>
          <KTextInput
            label="Vehicle Id"
            name="vehicle_id"
            value={vehicle_id}
            disabled
            rootClassName={classes.field}
            key="vehicle_id"
          />
        </div>
        <div className={classes.block}>
          <KTextInput
            label="Firmware Version"
            name="firmware_version"
            value={firmware_version}
            onChange={handleChange}
            placeholder="Firmware version"
            required={false}
            rootClassName={classes.field}
            key="firmware_version"
          />
          <KTextInput
            label="Hardware Version"
            name="hardware_version"
            value={hardware_version}
            onChange={handleChange}
            placeholder="Hardware version"
            required={false}
            rootClassName={classes.field}
            key="hardware_version"
          />
        </div>
        <div className={classes.block}>
          <KTextInput
            label="Created"
            name="created"
            value={formatDate(created)}
            disabled
            rootClassName={classes.field}
            key="created"
          />
          <KTextInput
            label="Modified"
            name="modified"
            value={formatDate(modified)}
            disabled
            rootClassName={classes.field}
            key="modified"
          />
        </div>
        <div className={classes.block}>
          <KTextInput
            label="Firmware Config File"
            name="firmware_config_file"
            value={firmwareConfigFileName}
            onChange={handleChange}
            placeholder={firmwareConfigFileName}
            disabled
            required={false}
            rootClassName={classes.field}
            endIcon="upload"
            key="firmware_config_file"
            onEndIconClick={onUploadClick}
          />
          <BaseUploader
            type="file"
            accept=".bin"
            ref={inputFile}
            onUpload={(e) => {
              if (e.target?.files && e.target?.files?.length > 0) {
                getIoTKeyDeviceConfigFileUrl(setState, e.target?.files[0], _id);
              }
            }}
          />
        </div>
      </KFormBackground>
    </BaseLayout>
  );
};

export default IoTKeyDeviceView;
